import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Check, Loader2 } from 'lucide-react';
import { invokeBot, listBots, type CallableBot } from '@/api/bots';
import type { UiMessage } from '@/api/inboxTypes';
import { Sheet } from '@/components/Sheet';
import { defaultBotMessages } from '@/lib/botProposal';
import { formatClock } from '@/lib/messageModel';
import { useToast } from '@/store/toast';

interface Props {
  accountId: number;
  convId: number;
  messages: UiMessage[];
  onClose: () => void;
}

function snippet(m: UiMessage): string {
  const t = (m.content ?? '').trim();
  if (t) return t.length > 90 ? `${t.slice(0, 90)}…` : t;
  const a = m.attachments?.[0];
  if (a?.file_type === 'audio') return 'Голосовое';
  if (a?.file_type === 'image') return 'Фото';
  if (a) return 'Файл';
  return 'Сообщение';
}

/**
 * «Позвать помощника» (прототип, botsSheet). Список — боты «по вызову», подключённые к каналу
 * (GET conversations/:id/unia_bots). Бот получает выбранные сообщения (обязательно хотя бы одно) и указание;
 * отвечает заметкой, а внешнее действие предлагает кнопками «Подтвердить / Отклонить» в самой переписке.
 */
export function BotsSheet({ accountId, convId, messages, onClose }: Props) {
  const toast = useToast((t) => t.show);
  const q = useQuery({ queryKey: ['cw', 'bots', accountId, convId], queryFn: () => listBots(accountId, convId), staleTime: 60_000 });
  const [bot, setBot] = useState<CallableBot | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);

  // Для выбора — 12 последних настоящих реплик (не заметки, не служебные); по умолчанию отмечены последние 5.
  const choices = useMemo(
    () => messages.filter((m) => m.id > 0 && !m.private && (m.message_type === 0 || m.message_type === 1)).slice(-12),
    [messages],
  );
  const [picked, setPicked] = useState<Set<number>>(() => new Set(defaultBotMessages(choices)));
  useEffect(() => { setPicked(new Set(defaultBotMessages(choices))); }, [choices.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: number) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const call = async () => {
    if (!bot || picked.size === 0 || busy) return;
    setBusy(true);
    try {
      await invokeBot(accountId, convId, { agentBotId: bot.id, messageIds: [...picked], instruction });
      toast(`${bot.name} получил задание. Ответ придёт в переписку`);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось позвать помощника');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Помощники" onClose={onClose} tall={!!bot}>
      <div className="sh"><h3>{bot ? bot.name : 'Позвать помощника'}</h3></div>
      <div className="sb" style={{ gap: bot ? 12 : 2 }}>
        {!bot && q.isPending && <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>}
        {!bot && q.isError && <p className="empty" role="alert">Не удалось загрузить список помощников: {q.error.message}</p>}
        {!bot && q.data && q.data.length === 0 && (
          <p className="empty">В этом канале нет помощников «по вызову». Их подключает администратор в настройках канала, вкладка «Конфигурация бота».</p>
        )}
        {!bot && q.data?.map((b) => (
          <button key={b.id} type="button" className="opt" onClick={() => setBot(b)}>
            <Bot aria-hidden />
            <span className="t">{b.name}{b.description ? <small>{b.description}</small> : null}</span>
          </button>
        ))}

        {bot && (
          <>
            <div>
              <div className="h4">О чём спросить помощника</div>
              <p className="pnote">Помощник увидит отмеченные сообщения и файлы в них. Нужно отметить хотя бы одно.</p>
            </div>
            <div role="group" aria-label="Сообщения для помощника" className="bmsgs">
              {choices.length === 0 && <p className="empty">В переписке пока нет сообщений.</p>}
              {choices.map((m) => (
                <button key={m.id} type="button" className={`bmsg${picked.has(m.id) ? ' on' : ''}`} aria-pressed={picked.has(m.id)} onClick={() => toggle(m.id)}>
                  <span className="bck">{picked.has(m.id) ? <Check aria-hidden /> : null}</span>
                  <span className="bt">{snippet(m)}<small>{m.message_type === 0 ? 'Клиент' : 'Мы'}, {formatClock(m.created_at)}</small></span>
                </button>
              ))}
            </div>
            <label className="ifield">
              <span>Указание (по желанию)</span>
              <textarea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Например: заведи заявку на двух водителей" />
            </label>
            <div className="btnrow">
              <button type="button" className="ghost" onClick={() => setBot(null)} disabled={busy}>Назад</button>
              <button type="button" className="primary" onClick={() => void call()} disabled={busy || picked.size === 0}>
                {busy ? <Loader2 className="spin-i" aria-hidden /> : <Bot aria-hidden />}Позвать
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
